/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, Point, useDictionary, useParameter } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { getDateParts } from "../utils/dateHelpers";
import { first, uniqBy } from "lodash";
import { dictionaryService } from "../../../dataFlow";
import { SLIDE_NUMBER } from "../constants";

const CATEGORIES_AXIS = "commodities.default";
const SERIES_AXIS = "calendar.default";
const UNITS_AXIS = "units.default";
const [YEAR_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY] = [1, 4, 7];

export function useHighchartTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [selectedCommodityId] = useParameter<number>("@selected_commodity_id");
  const [selectedUnitId] = useParameter<number>("@selected_unit_id");

  const [commodities] = useParameter<number[]>("@commodities");
  const [commoditiesColumnsDisplay] = useDictionary(dictionaryService, "slides-commodities-display", undefined, {
    slide: SLIDE_NUMBER,
    business_unit: 1,
  });
  const [unitDictItems] = useDictionary(dictionaryService, "f_units_ap_official_prices", undefined);

  const uniqItems = useMemo(
    () =>
      uniqBy(
        olapData?.rows?.flatMap((row) => row.points),
        (point) => {
          if (point?.attributes) {
            return JSON.stringify(point?.attributes);
          }
        },
      ),
    [olapData],
  );

  const commodityItem = useMemo(() => {
    let commodity: Point | undefined;

    if (selectedCommodityId != null && selectedUnitId != null) {
      commodity = uniqItems.find((item) => {
        const commodityId = item?.attributes && item?.attributes[`${CATEGORIES_AXIS}.id`];
        const unitId = item?.attributes && item?.attributes[`${UNITS_AXIS}.id`];
        return Number(commodityId) === selectedCommodityId && Number(unitId) === selectedUnitId;
      });
    } else {
      const firstAvailableCommodityId = first(commodities);
      commodity = uniqItems.find((item) => {
        const id = item?.attributes && item?.attributes[`${CATEGORIES_AXIS}.id`];
        return Number(id) === firstAvailableCommodityId;
      });
    }

    if (commodity?.attributes) {
      let name = commodity?.attributes[`${CATEGORIES_AXIS}.name`];
      if (name == null) {
        const displayItem = commoditiesColumnsDisplay?.find(
          (item) => item.getValue<number>("olap_commodity") === Number(selectedCommodityId),
        );
        name = displayItem?.getValue<any>("name");
      }

      const unitId = commodity?.attributes[`${UNITS_AXIS}.id`];
      let unitName = commodity?.attributes[`${UNITS_AXIS}.name`];
      if (unitName == null) {
        const displayItem = unitDictItems?.find((item) => item.getValue<number>("id") === Number(selectedUnitId));
        unitName = displayItem?.getValue<any>("name");
      }

      return {
        commodityId: commodity?.attributes[`${CATEGORIES_AXIS}.id`],
        name: typeof name === "object" ? name[9] : name,
        unitId,
        unitName: typeof unitName === "object" ? unitName[9] : unitName,
      };
    } else {
      const unitCandidate = unitDictItems?.find((item) => item.getValue<number>("id") === Number(selectedUnitId));
      const unitId = unitCandidate?.getValue<any>("id");
      const unitName = unitCandidate?.getValue<any>("name");
      const commodityCandidate = commoditiesColumnsDisplay?.find(
        (item) => item.getValue<number>("olap_commodity") === Number(selectedCommodityId),
      );
      const commodityName = commodityCandidate?.getValue<any>("name");
      const commodityId = commodityCandidate?.getValue<any>("olap_commodity");

      return {
        unitId,
        commodityId,
        name: typeof commodityName === "object" ? commodityName[9] : commodityName,
        unitName: typeof unitName === "object" ? unitName[9] : unitName,
      };
    }
  }, [selectedCommodityId, selectedUnitId, uniqItems, commoditiesColumnsDisplay]);

  const olapWeeklyRows = useMemo(
    () =>
      olapData?.rows.filter((olapRow) => {
        const calendarId = olapRow.attributes[`${SERIES_AXIS}.id`];
        const unitId = olapRow?.attributes && olapRow?.attributes[`${UNITS_AXIS}.id`];

        const { frequency } = getDateParts(calendarId);

        return Number(frequency) === WEEK_FREQUENCY && Number(unitId) === Number(commodityItem?.unitId);
      }),
    [olapData, commodityItem],
  );

  const [weekCategories] = useMemo(
    () =>
      [WEEK_FREQUENCY].map((frequency) => {
        const frequencyColumns = olapWeeklyRows?.filter((row) => {
          const colFrequency = +String(row.selection[SERIES_AXIS])[0];
          return colFrequency === frequency;
        });

        return frequencyColumns?.map((column) => {
          const name = column?.attributes[`${SERIES_AXIS}.name`];
          const unit = column?.attributes[`${UNITS_AXIS}.name`][9] || "";

          return {
            name,
            unit,
          };
        });
      }) || [],
    [olapWeeklyRows],
  );

  const options = useChartOptions(
    {
      tooltip: {
        useHTML: true,
        formatter: function() {
          const { category, unit } = this.point as any;
          const pointValue = Highcharts.numberFormat(this.point.y || 0, 2);
          return `
            <div>
              <b>${this.point.series.name}</b><br/>
              <b>${category}</b>: <span>${pointValue} ${unit}</span>
            </div>
          `;
        },
        outside: true,
        style: {
          zIndex: 100,
        },
      },
    },
    [
      chartPipe("spline"),
      legendPipe("bottom", {
        useHTML: true,
        labelFormatter: function() {
          const { name } = this;

          return name;
        },
      }),
      addXAxisPipe(
        weekCategories?.map((category) => category.name),
        {
          visible: true,
          labels: {
            enabled: true,
            formatter: function() {
              const label = this.axis.defaultLabelFormatter.call(this);
              return label;
            },
          },
        },
      ),
      addYAxisPipe({
        gridLineColor: "#fff",
        title: {
          text: "",
        },
        labels: {
          enabled: true,
          formatter: function() {
            const label = this.axis.defaultLabelFormatter.call(this);
            if (/^[0-9]{4}$/.test(label)) {
              return Highcharts.numberFormat(this.value, 0);
            }
            return label;
          },
        },
      }),
    ],
  );

  useEffect(() => {
    if (olapData) {
      const colors = ["#1CA955", "#E38114", "#DD5C3D", "#8F8F8F", "#D9D10C", "#2D5EDA", "#784FBA", "#D656A3"];

      const series: SeriesOptionsType[] = [
        {
          name: [commodityItem?.name, commodityItem?.unitName].filter(Boolean).join(", "),
          type: "spline",
          data: olapWeeklyRows?.map((row, rowIdx) => {
            const point = row.points.find((p) => {
              if (p?.attributes) {
                return p?.attributes[`${CATEGORIES_AXIS}.id`] === commodityItem?.commodityId;
              }
            });
            const value = point?.value || null;
            const date = (point?.attributes && point?.attributes[`${SERIES_AXIS}.name`]) ?? "";
            const unit = (point?.attributes && point?.attributes[`${UNITS_AXIS}.name`][9]) ?? "";

            return {
              date,
              unit,
              x: rowIdx,
              y: value,
              isLast: rowIdx === olapWeeklyRows.length - 1,
            };
          }),
          yAxis: 0,
          xAxis: 0,
          color: colors[Math.floor(Math.random() * colors.length)],
          zIndex: 2,
          marker: {
            radius: 0,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
        },
      ];

      const result: Highcharts.Options = {
        ...options,
        series,
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapWeeklyRows, commodityItem]);

  return state;
}
